/**
 * notification-toast.js
 *
 * Obsługuje wyświetlanie powiadomień toast (success / error / info / warning).
 *
 * Publiczne API:
 *   window.NotificationToast.show(message, type)
 *     @param {string} message  - Treść powiadomienia (plain text, sanitizowana)
 *     @param {string} [type]   - 'success' | 'error' | 'info' | 'warning' (domyślnie 'info')
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SEKCJA WYZWALACZY — zdefiniuj tutaj warunki wyświetlania powiadomień:
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const DURATION_MS = 3000;

  /* ─── Klasa ToastManager ───────────────────────────────────────────────── */

  class ToastManager {
    constructor() {
      this.container = document.getElementById('toast-container');
      if (!this.container) return;

      // Pobierz adresy URL ikon z atrybutów data kontenera
      this.icons = {
        success: this.container.dataset.successIcon || '',
        error:   this.container.dataset.errorIcon   || '',
        info:    this.container.dataset.infoIcon    || '',
        warning: this.container.dataset.warningIcon || '',
      };
      this.closeLabel = this.container.dataset.closeLabel || '';
    }

    /**
     * Wyświetla nowe powiadomienie toast.
     * @param {string} message
     * @param {'success'|'error'|'info'|'warning'} [type='info']
     */
    show(message, type = 'info') {
      if (!this.container) return;
      const allowedTypes = ['success', 'error', 'info', 'warning'];
      const safeType = allowedTypes.includes(type) ? type : 'info';
      const toast = this._createToast(this._sanitize(message), safeType);
      this.container.appendChild(toast);
      this._startTimer(toast);
    }

    /* ─── Tworzenie elementu DOM powiadomienia ────────────────────────── */

    _createToast(message, type) {
      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.setAttribute('role', 'alert');

      // Ikona
      const iconEl = document.createElement('div');
      iconEl.className = 'toast__icon';
      if (this.icons[type]) {
        const img = document.createElement('img');
        img.src = this.icons[type];
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        iconEl.appendChild(img);
      }

      // Komunikat
      const msgEl = document.createElement('div');
      msgEl.className = 'toast__message';
      msgEl.textContent = message;

      // Przycisk zamknięcia
      const closeBtn = document.createElement('button');
      closeBtn.className = 'toast__close';
      closeBtn.setAttribute('aria-label', this.closeLabel);
      closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
      </svg>`;

      // Pasek postępu
      const progressEl = document.createElement('div');
      progressEl.className = 'toast__progress';

      toast.appendChild(iconEl);
      toast.appendChild(msgEl);
      toast.appendChild(closeBtn);
      toast.appendChild(progressEl);

      // Zdarzenia
      closeBtn.addEventListener('click', () => this._dismiss(toast));
      toast.addEventListener('mouseenter', () => this._pauseTimer(toast));
      toast.addEventListener('mouseleave', () => this._resumeTimer(toast));

      return toast;
    }

    /* ─── Timer i pasek postępu ───────────────────────────────────────── */

    _startTimer(toast) {
      const progressEl = toast.querySelector('.toast__progress');

      const state = {
        elapsed:   0,      // ms już upłynęły
        lastTick:  null,   // timestamp ostatniego klatku rAF
        paused:    false,
        rafId:     null,
      };

      toast._toastTimer = state;

      const tick = (now) => {
        if (state.paused) return;

        if (state.lastTick !== null) {
          state.elapsed += now - state.lastTick;
        }
        state.lastTick = now;

        const progress = Math.max(0, 1 - state.elapsed / DURATION_MS);
        progressEl.style.transform = `scaleX(${progress})`;

        if (state.elapsed >= DURATION_MS) {
          this._dismiss(toast);
          return;
        }

        state.rafId = requestAnimationFrame(tick);
      };

      // Przechowaj referencję do tick, by móc wznowić animację
      state.tick = tick;
      state.rafId = requestAnimationFrame(tick);
    }

    _pauseTimer(toast) {
      const state = toast._toastTimer;
      if (!state || state.paused) return;
      state.paused = true;
      if (state.rafId !== null) {
        cancelAnimationFrame(state.rafId);
        state.rafId = null;
      }
    }

    _resumeTimer(toast) {
      const state = toast._toastTimer;
      if (!state || !state.paused) return;
      state.paused = false;
      state.lastTick = null; // Reset, by nie dodać czasu pauzy do elapsed
      state.rafId = requestAnimationFrame(state.tick);
    }

    /* ─── Usuwanie powiadomienia ──────────────────────────────────────── */

    _dismiss(toast) {
      if (!toast || toast._dismissing) return;
      toast._dismissing = true;

      // Zatrzymaj timer
      const state = toast._toastTimer;
      if (state && state.rafId !== null) {
        cancelAnimationFrame(state.rafId);
      }

      // Zafixuj aktualną wysokość przed animacją, by zwijanie było płynne
      toast.style.maxHeight = toast.offsetHeight + 'px';

      // Wymuszenie reflow, potem dodanie klasy animacji
      // eslint-disable-next-line no-unused-expressions
      toast.offsetHeight;
      toast.classList.add('toast--removing');

      // Usuń element po zakończeniu animacji zwijania
      const removeToast = () => {
        toast.removeEventListener('transitionend', onEnd);
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      };

      const onEnd = (event) => {
        if (event.target !== toast || event.propertyName !== 'max-height') return;
        removeToast();
      };
      toast.addEventListener('transitionend', onEnd);

      // Fallback na wypadek, gdyby transitionend nie wystrzelił
      setTimeout(() => {
        removeToast();
      }, 500);
    }

    /* ─── Sanityzacja tekstu (ochrona przed XSS) ─────────────────────── */

    _sanitize(str) {
      const div = document.createElement('div');
      div.textContent = String(str);
      return div.textContent;
    }
  }

  /* ─── Inicjalizacja ───────────────────────────────────────────────────── */

  let manager;

  function init() {
    manager = new ToastManager();
    window.NotificationToast = {
      show: (message, type) => manager.show(message, type),
    };
    runTriggers();
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * SEKCJA WYZWALACZY
   * Dodaj tutaj kolejne warunki uruchamiające powiadomienia.
   * ═══════════════════════════════════════════════════════════════════════ */

  function runTriggers() {
    const search = window.location.search;
    const hash   = window.location.hash;

    const isElementVisible = (element) => {
      if (!(element instanceof HTMLElement)) return false;

      if (typeof element.checkVisibility === 'function') {
        return element.checkVisibility({
          checkOpacity: true,
          checkVisibilityCSS: true,
        });
      }

      return element.offsetParent !== null;
    };

    /* ── Wyzwalacz 1: Formularz newslettera — zapis potwierdzony ───────
     * Warunek: URL zawiera "customer_posted=true" w query string
     *          ORAZ "#contact_form" w hashu.
     * Wyświetla powiadomienie sukcesu z prośbą o potwierdzenie e-mail.
     * Po wyświetleniu usuwa parametry wyzwalające z adresu, aby
     * powiadomienie nie pojawiało się ponownie po odświeżeniu.
     * ─────────────────────────────────────────────────────────────────── */
    //if (search.includes('customer_posted=true') && hash.includes('#contact_form')) {
    //  manager.show(
    //    'Dziękujemy! Sprawdź swój email i potwierdź zapis do naszego newslettera.',
    //    'success'
    //  );          
    //}

    /* ── Wyzwalacz 2: Elementy DOM z atrybutem data-toast-message ───────
     * Warunek: dowolny element na stronie posiada atrybut data-toast-message.
     * Typ powiadomienia określa data-toast-type (domyślnie 'info').
     * Użycie: dodaj do elementu HTML atrybuty:
     *   data-toast-message="Treść komunikatu"
     *   data-toast-type="success|error|info|warning"
     * ─────────────────────────────────────────────────────────────────── */
    const toastTriggers = document.querySelectorAll('[data-toast-message]');
    toastTriggers.forEach((el) => {
      if (!isElementVisible(el)) return;
      const message = (el.dataset.toastMessage || '').trim();
      if (!message) return;
      const type = (el.dataset.toastType || 'info').trim();
      manager.show(message, type);
    });

    /* ── Wyzwalacz 2: Dodaj kolejne warunki poniżej ──────────────────── */
    // Przykład:
    // if (search.includes('added_to_cart=true')) {
    //   manager.show('Produkt dodany do koszyka!', 'success');
    // }
  }

  /* ─── Uruchom po załadowaniu DOM ──────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
